import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startLoadingCatalogsAll } from '../actions/catalogActions';
import { startLoadingCatalogCiiusAll, startLoadingCatalogCiiusByLevel1 } from '../actions/catalogCiiuActions';
import { startLoadingCatalogSectionsAll } from '../actions/catalogSectionActions';
import { startLoadingCatalogPhasesAll } from '../actions/catalogPhaseActions';
import { startLoadingContinents } from '../actions/continentActions';
import { startLoadingRegionByContinentIdLocal, startLoadingRegions } from '../actions/regionActions';
import { getContinentRegionCountryByCountryId, startLoadingCountries, startLoadingCountriesByRegionIdLocal } from '../actions/countryActions';
import {
  getObjectByIdHelper,
  getSelectedCatalogsByDescriptionHelper,
  getSelectedProblemsOriginalCatalogsHelper,
  hydrateMultilevelSelectFields,
  stringToListHelper
} from './helperCatalogs';
import { reloadModuleRecord } from './flowEditPageHelper';
import { mergePreservingMultilevelSelects } from './helperConcat';

const PROBLEM_ALIAS = {
  technology: 'ModProblem_To_Technology',
  alternative: 'ModProblem_To_Alternative',
  experience: 'ModProblem_To_Experience',
  regulation: 'ModProblem_To_Regulation'
};

const FILE_ALIAS = {
  technology: 'ModuleFile_To_Technology',
  alternative: 'ModuleFile_To_Alternative',
  experience: 'ModuleFile_To_Experience',
  innovation: 'ModuleFile_To_Innovation',
  assistance: 'ModuleFile_To_Assistance',
  financial: 'ModuleFile_To_Financial',
  regulation: 'ModuleFile_To_Regulation'
};

const ADAPTATION_FIELDS = [
  { field: 'general_adaptation', listField: 'general_adaptation_list', catalogKey: 'listGeneralAdaptations' },
  { field: 'structural_adaptation', listField: 'structural_adaptation_list', catalogKey: 'listStructuralAdaptations' },
  { field: 'specific_adaptation', listField: 'specific_adaptation_list', catalogKey: 'listSpecificAdaptations' }
];

const isCatalogObjectList = (value) => (
  Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null
);

const isLocationObject = (value) => (
  Boolean(value) && typeof value === 'object' && Number(value.id) > 0
);

const resolveLocationValue = (value, list = []) => {
  if (isLocationObject(value)) {
    return value;
  }
  return getObjectByIdHelper(value, list) || value;
};

const locationNumericId = (value) => {
  if (isLocationObject(value)) {
    return Number(value.id);
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
};

const hydrateAdaptationField = (record, field, listField, catalogList) => {
  if (isCatalogObjectList(record[listField])) {
    record[field] = record[listField];
    return;
  }
  if (isCatalogObjectList(record[field])) {
    record[listField] = record[field];
    return;
  }
  if (!Array.isArray(catalogList) || catalogList.length === 0) {
    return;
  }
  const texts = Array.isArray(record[field])
    ? record[field]
    : stringToListHelper(record[field]);
  if (texts.length === 0) {
    return;
  }
  const resolved = getSelectedCatalogsByDescriptionHelper(texts, catalogList);
  record[listField] = resolved;
  record[field] = resolved;
};

export const hydrateModuleFormRelations = (record, process, catalogs = {}) => {
  if (!record) {
    return record;
  }

  const next = { ...record };
  const moduleKey = String(process || '').toLowerCase();
  const problemKey = PROBLEM_ALIAS[moduleKey];

  ADAPTATION_FIELDS.forEach(({ field, listField, catalogKey }) => {
    hydrateAdaptationField(next, field, listField, catalogs[catalogKey]);
  });

  if (problemKey && Array.isArray(next[problemKey]) && next[problemKey].length > 0) {
    next.catalogproblem_list = getSelectedProblemsOriginalCatalogsHelper(
      next[problemKey],
      catalogs.listProblems
    );
  }

  next.catalogconcern_id = resolveLocationValue(next.catalogconcern_id, catalogs.listConcerns);
  next.catalogpermissionpublish_id = resolveLocationValue(
    next.catalogpermissionpublish_id,
    catalogs.listPermissionPublish
  );
  next.catalogcontinents_id = resolveLocationValue(next.catalogcontinents_id, catalogs.listContinents);
  next.catalogregions_id = resolveLocationValue(next.catalogregions_id, catalogs.listRegions);
  next.catalogcountries_id = resolveLocationValue(next.catalogcountries_id, catalogs.listCountries);

  return next;
};

export const useHydrateModuleEditRecord = ({
  moduleProcess,
  selectedRecord,
  setValues,
  selectAction,
  catalogs = {}
}) => {
  const dispatch = useDispatch();
  const hydratedSigRef = useRef('');
  const locationCountry = useSelector((state) => state.locationCountry || {});
  const listContinents = locationCountry.listContinents || [];
  const listRegions = locationCountry.listFullRegions?.length
    ? locationCountry.listFullRegions
    : (locationCountry.listRegions || []);
  const listCountries = locationCountry.listFullCountries?.length
    ? locationCountry.listFullCountries
    : (locationCountry.listCountries || []);
  const listFullCatalogCiius = useSelector((state) => state.catalogCiiu?.listFullCatalogCiius || []);
  const listFullCatalogSections = useSelector((state) => state.catalogSection?.listFullCatalogSections || []);
  const listFullCatalogPhases = useSelector((state) => state.catalogPhase?.listFullCatalogPhases || []);
  const objectContinentSelectedInitial = locationCountry.objectContinentSelectedInitial;
  const objectRegionSelectedInitial = locationCountry.objectRegionSelectedInitial;
  const objectCountrySelectedInitial = locationCountry.objectCountrySelectedInitial;

  useEffect(() => {
    dispatch(startLoadingCatalogsAll());
    dispatch(startLoadingCatalogCiiusAll());
    dispatch(startLoadingCatalogCiiusByLevel1(1));
    dispatch(startLoadingCatalogSectionsAll());
    dispatch(startLoadingCatalogPhasesAll());
    dispatch(startLoadingContinents());
    dispatch(startLoadingRegions());
    dispatch(startLoadingCountries());
  }, [dispatch]);

  useEffect(() => {
    const recordId = selectedRecord?.id;
    if (!recordId || typeof setValues !== 'function' || typeof selectAction !== 'function') {
      return undefined;
    }

    const catalogSig = [
      catalogs.listProblems?.length || 0,
      catalogs.listConcerns?.length || 0,
      catalogs.listPermissionPublish?.length || 0,
      catalogs.listGeneralAdaptations?.length || 0,
      catalogs.listStructuralAdaptations?.length || 0,
      catalogs.listSpecificAdaptations?.length || 0,
      listContinents.length,
      listRegions.length,
      listCountries.length,
      listFullCatalogCiius.length,
      listFullCatalogSections.length,
      listFullCatalogPhases.length
    ].join(':');
    const sig = `${moduleProcess}:${recordId}:${catalogSig}`;
    if (hydratedSigRef.current === sig) {
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const problemKey = PROBLEM_ALIAS[String(moduleProcess || '').toLowerCase()];
      const fileKey = FILE_ALIAS[String(moduleProcess || '').toLowerCase()];
      const missingRelations = (
        (problemKey && selectedRecord[problemKey] === undefined)
        || (fileKey && selectedRecord[fileKey] === undefined)
      );
      const missingLocation = (
        !isLocationObject(selectedRecord.catalogcountries_id)
        && !isLocationObject(selectedRecord.catalogcontinents_id)
      );

      let base = selectedRecord;
      if (missingRelations || missingLocation) {
        const fetched = await reloadModuleRecord(moduleProcess, recordId);
        if (fetched) {
          base = fetched;
        }
      }

      if (cancelled) {
        return;
      }

      const countryId = locationNumericId(base.catalogcountries_id);
      if (countryId > 0) {
        dispatch(getContinentRegionCountryByCountryId(countryId));
      }

      const hydrated = hydrateModuleFormRelations(base, moduleProcess, {
        ...catalogs,
        listContinents,
        listRegions,
        listCountries
      });
      Object.assign(
        hydrated,
        hydrateMultilevelSelectFields({
          values: hydrated,
          catalogList: listFullCatalogCiius,
          prefix: 'catalogciiu',
          idField: 'catalogciius_id',
          levelsField: 'catalogciiu_levels',
          maxLevels: 6
        }),
        hydrateMultilevelSelectFields({
          values: hydrated,
          catalogList: listFullCatalogSections,
          prefix: 'catalogsection',
          idField: 'catalogsections_id',
          levelsField: 'catalogsection_levels',
          maxLevels: 3
        }),
        hydrateMultilevelSelectFields({
          values: hydrated,
          catalogList: listFullCatalogPhases,
          prefix: 'catalogphase',
          idField: 'catalogphases_id',
          levelsField: 'catalogphase_levels',
          maxLevels: 5
        })
      );
      hydratedSigRef.current = sig;
      dispatch(selectAction(hydrated));
      setValues((prev) => mergePreservingMultilevelSelects(prev, hydrated));
    };

    run();

    return () => {
      cancelled = true;
    };
    // catalogs is a new object each render; depend on the lists instead.
  }, [
    moduleProcess,
    selectedRecord,
    setValues,
    selectAction,
    catalogs.listProblems,
    catalogs.listConcerns,
    catalogs.listPermissionPublish,
    catalogs.listGeneralAdaptations,
    catalogs.listStructuralAdaptations,
    catalogs.listSpecificAdaptations,
    listContinents,
    listRegions,
    listCountries,
    listFullCatalogCiius,
    listFullCatalogSections,
    listFullCatalogPhases,
    dispatch
  ]);

  useEffect(() => {
    if (typeof setValues !== 'function') {
      return;
    }
    const continent = isLocationObject(objectContinentSelectedInitial)
      ? objectContinentSelectedInitial
      : null;
    const region = isLocationObject(objectRegionSelectedInitial)
      ? objectRegionSelectedInitial
      : null;
    const country = isLocationObject(objectCountrySelectedInitial)
      ? objectCountrySelectedInitial
      : null;
    if (!continent && !region && !country) {
      return;
    }
    if (continent) {
      dispatch(startLoadingRegionByContinentIdLocal(continent.id));
    }
    if (region) {
      dispatch(startLoadingCountriesByRegionIdLocal(region.id));
    }
    setValues((prev) => {
      const currentCountryId = locationNumericId(prev?.catalogcountries_id);
      const initialCountryId = locationNumericId(country);
      if (initialCountryId && currentCountryId && initialCountryId !== currentCountryId) {
        return prev;
      }
      if (!currentCountryId && !locationNumericId(prev?.catalogcontinents_id)) {
        return prev;
      }
      return {
        ...(prev || {}),
        ...(continent ? { catalogcontinents_id: continent } : {}),
        ...(region ? { catalogregions_id: region } : {}),
        ...(country ? { catalogcountries_id: country } : {})
      };
    });
  }, [
    dispatch,
    objectContinentSelectedInitial,
    objectCountrySelectedInitial,
    objectRegionSelectedInitial,
    locationCountry.listFullCountries,
    locationCountry.listFullRegions,
    setValues
  ]);
};
